import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TYPE_LABEL_KEY, type StudentQuestion, type StudentTask } from '@/api/ieltsMock';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { LegacyTaskCard } from '@/components/ielts/LegacyTaskCard';
import { BankBody } from '@/components/ielts/BankBody';
import { LayoutBody } from '@/components/ielts/LayoutBody';
import { MatchGrid } from '@/components/ielts/MatchGrid';
import { bankLayout } from '@/lib/bankBox';
import { matchGridLetters, splitNB } from '@/lib/matchGrid';
import { useIsMobile } from '@/hooks/useIsMobile';
import { choiceLabel, inlinePrompt, twoColumns, wideKind, type Piece } from '@/lib/taskLayout';
import { questionNumber } from '@/lib/mockReview';
import { multiChosen, multiFull, multiToggle } from '@/lib/multiChoice';
import { serverLayout } from '@/lib/serverLayout';
import { cn } from '@/lib/utils';

/**
 * Карточка задания мока и спринта (вынесена из StudentIELTSMockPage 21.10.2026, T4).
 *
 * На компьютере у заданий с полем и у выбора A/B/C — устройство как на бланке IELTS: поле ответа
 * внутри строки на месте «____», перед полем квадратик с номером; заметки и резюме — в рамке
 * пунктами; форма и таблица — две колонки «фраза | поле»; выбор — буквы A/B/C; «выберите ДВА» —
 * буквы A–E и флажки, не больше двух (T3, 30.10.2026).
 *
 * Узкий экран (< 768 px — приложение и сайт в телефоне) — то же устройство с 27.10.2026 (iOS 1.1.2,
 * слово владельца: «в 1.1.2 всё как на сайте»; до этого телефон оставался на прежней карточке).
 * Отличие одно: форма и таблица на узком — одной колонкой (фраза, под ней поле), две колонки
 * на 390 уезжали вбок. Поле над клавиатурой в приложении держит `lib/keyboard.ts`.
 *
 * Matching information с буквами абзацев — сетка «утверждение × буква», на телефоне ряд букв (`MatchGrid`,
 * T3, 30.10.2026); строка «NB …» инструкции — отдельной строкой у любого вида.
 *
 * Прочее сопоставление, True/False/Not Given — прежняя карточка (`LegacyTaskCard`) на любой
 * ширине, как на сайте. Структура задания с сервера (`layout`) — потом, формат в
 * docs/tasks/T4-mock-task-layout.md.
 *
 * Банк фраз (`summary_completion_bank`) со сходящейся вёрсткой — абзац и рамка фраз A–J, касание и
 * перетаскивание (`BankBody`, T3, 29.10.2026); без вёрстки — прежняя карточка с выпадающим списком.
 *
 * Значения ответов НЕ менялись: текст у поля, текст варианта у выбора, текст фразы у банка.
 */

type TaskCardProps = {
  task: StudentTask;
  answers: Record<string, string>;
  onAnswer: (qid: string, val: string) => void;
};

export function TaskCard(props: TaskCardProps) {
  return wideKind(props.task.type) || bankLayout(props.task) || matchGridLetters(props.task) ? (
    <WideTaskCard {...props} />
  ) : (
    <LegacyTaskCard {...props} />
  );
}

const LETTERS = 'ABCDEFGHIJKLMNOP'.split('');

/** Квадратик с номером вопроса, как на бланке. Он же подпись поля для чтения с экрана. */
function NumBox({ n, id }: { n: string; id?: string }) {
  return (
    <span
      id={id}
      className="inline-flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded border-2 border-on-surface/80 bg-surface px-1 text-sm font-bold tabular-nums leading-none text-on-surface"
    >
      {n}
    </span>
  );
}

/**
 * Поле ответа в строке: [номер][поле]. Номер и поле не разрываются переносом (nowrap), а в колонке
 * уже самого поля (заметки в рамке на 320–390) поле ужимается (`max-w-full` + `min-w-0`), а не
 * вылезает за край карточки.
 */
function GapInput({ q, value, onAnswer }: { q: StudentQuestion; value: string; onAnswer: (v: string) => void }) {
  const numId = `gap-n-${q.id}`;
  return (
    <span className="mx-1 inline-flex max-w-full items-center gap-1 whitespace-nowrap align-middle">
      <NumBox n={questionNumber(q.ord, q.ord_to)} id={numId} />
      <input
        type="text"
        value={value}
        onChange={(e) => onAnswer(e.target.value)}
        aria-labelledby={numId}
        // На экзамене орфография — часть ответа: подсказки браузера и автозамена делали бы её за ученика.
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="h-8 w-36 min-w-0 rounded border border-outline bg-surface px-2 text-sm text-on-surface focus:border-primary focus:outline-none"
      />
    </span>
  );
}

function WideTaskCard({ task, answers, onAnswer }: TaskCardProps) {
  const { t } = useTranslation();
  const narrow = useIsMobile();
  const kind = wideKind(task.type);

  const pieces = (ps: Piece[], q: StudentQuestion) =>
    ps.map((p, i) =>
      p.kind === 'text' ? (
        <Fragment key={i}>{p.text}</Fragment>
      ) : (
        <GapInput key={i} q={q} value={answers[q.id] ?? ''} onAnswer={(v) => onAnswer(q.id, v)} />
      ),
    );

  // 🔴 Страховка: в тексте вопроса не ровно один «____» — вопрос как раньше, с отдельным полем
  // под ним. Ни один вопрос не остаётся без поля (проверка — taskLayout.test.ts).
  const oldRow = (q: StudentQuestion): ReactNode => (
    <div className="space-y-2">
      <div className="flex gap-2 text-sm font-medium">
        <span className="text-primary">{questionNumber(q.ord, q.ord_to)}.</span>
        <span>{q.prompt}</span>
      </div>
      {/* Отступ — у обёртки: `ml-5` + `w-full` у самого поля давали ширину 100% + 20px, и на 390 поле
          вылезало за край карточки. */}
      <div className="pl-5">
        <input
          type="text"
          value={answers[q.id] ?? ''}
          onChange={(e) => onAnswer(q.id, e.target.value)}
          placeholder={t('ieltsMock.yourAnswer')}
          className="w-full max-w-sm rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
        />
      </div>
    </div>
  );

  const inline = (q: StudentQuestion): ReactNode => {
    const ps = inlinePrompt(q.prompt);
    return ps ? pieces(ps, q) : oldRow(q);
  };

  // Вёрстка с сервера (T3, 28.10.2026): заметки и резюме как на листе экзамена. Только если сходится с
  // вопросами (lib/serverLayout.ts) — иначе как раньше, построчно.
  const layout = kind === 'notes' || kind === 'lines' ? serverLayout(task.layout, task.questions) : null;
  const bank = bankLayout(task);
  const gridLetters = matchGridLetters(task);

  let body: ReactNode;
  if (gridLetters) {
    body = (
      <MatchGrid
        task={task}
        letters={gridLetters}
        answers={answers}
        onAnswer={onAnswer}
        narrow={narrow}
        num={(q) => <NumBox n={questionNumber(q.ord, q.ord_to)} />}
      />
    );
  } else if (bank) {
    body = (
      <BankBody
        layout={bank}
        options={task.options}
        answers={answers}
        onAnswer={onAnswer}
        num={(q) => <NumBox n={questionNumber(q.ord, q.ord_to)} />}
      />
    );
  } else if (layout) {
    body = (
      <LayoutBody layout={layout} gap={(q) => <GapInput q={q} value={answers[q.id] ?? ''} onAnswer={(v) => onAnswer(q.id, v)} />} />
    );
  } else if (kind === 'notes') {
    // Заметки и резюме — рамка, вопросы пунктами.
    body = (
      <div className="rounded-lg border border-outline bg-surface/60 p-4">
        <ul className="list-disc space-y-1 pl-5 text-sm leading-9 text-on-surface">
          {task.questions.map((q) => (
            <li key={q.id}>{inline(q)}</li>
          ))}
        </ul>
      </div>
    );
  } else if (kind === 'columns' && narrow) {
    // Форма и таблица на узком экране — одна колонка: фраза, под ней поле. Те же две части, что
    // у таблицы на компьютере (`twoColumns`), и та же страховка. Пропуск посреди фразы без «:»
    // («Which course …? ____ course») — поле на своём месте в строке: в одной колонке «… course»
    // над полем читалось бы загадкой (стенд 27.10, кадр 390).
    body = (
      <ul className="space-y-2 text-sm text-on-surface">
        {task.questions.map((q) => {
          const cols = twoColumns(q.prompt);
          const ps = inlinePrompt(q.prompt);
          const tail = ps?.[ps.length - 1];
          const midGap = !!cols && !cols.left.endsWith(':') && tail?.kind === 'text' && !/^[\s.,;!?]*$/.test(tail.text);
          return (
            <li key={q.id} className="rounded-lg border border-outline px-3 py-2">
              {midGap && ps ? (
                <div className="leading-9">{pieces(ps, q)}</div>
              ) : cols ? (
                <>
                  <div className="font-medium">{cols.left}</div>
                  <div className="leading-9">{pieces(cols.right, q)}</div>
                </>
              ) : (
                oldRow(q)
              )}
            </li>
          );
        })}
      </ul>
    );
  } else if (kind === 'columns') {
    // Форма и таблица — две колонки «фраза | поле».
    body = (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm text-on-surface">
          <tbody>
            {task.questions.map((q) => {
              const cols = twoColumns(q.prompt);
              return (
                <tr key={q.id}>
                  {cols ? (
                    <>
                      <td className="w-1/2 border border-outline px-3 py-2 align-middle">{cols.left}</td>
                      <td className="border border-outline px-3 py-2 align-middle leading-9">{pieces(cols.right, q)}</td>
                    </>
                  ) : (
                    <td colSpan={2} className="border border-outline px-3 py-2">
                      {oldRow(q)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  } else if (kind === 'choice') {
    // Выбор A/B/C: номер-квадратик, буквы в кружках, ответ — текст варианта. Вся строка варианта —
    // <label>, тап по букве или тексту выбирает его; на узком строка выше (py-1.5) — под палец.
    // «Выберите ДВА» (mc_multi, номера «21–22») — те же буквы A–E, флажки: третий не встаёт, значение —
    // варианты по алфавиту через « || », как ждёт сервер (lib/multiChoice.ts).
    const multi = task.type === 'mc_multi';
    body = (
      <ol className="space-y-5">
        {task.questions.map((q) => {
          const val = answers[q.id] ?? '';
          return (
            <li key={q.id} className="space-y-2">
              <div className="flex items-start gap-2 text-sm font-medium text-on-surface">
                <NumBox n={questionNumber(q.ord, q.ord_to)} />
                <span className="pt-0.5">{q.prompt}</span>
              </div>
              {(q.options ?? []).map((opt, i) => {
                const full = multi && multiFull(val, opt);
                return (
                  <label
                    key={i}
                    className={cn('flex items-start gap-2 py-1.5 pl-4 text-sm md:py-0', full ? 'opacity-50' : 'cursor-pointer')}
                  >
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-xs font-semibold text-on-surface">
                      {LETTERS[i]}
                    </span>
                    {multi ? (
                      <input
                        type="checkbox"
                        checked={multiChosen(val).includes(opt)}
                        disabled={full}
                        onChange={() => onAnswer(q.id, multiToggle(val, opt))}
                        className="mt-1 accent-primary"
                      />
                    ) : (
                      <input
                        type="radio"
                        name={q.id}
                        checked={val === opt}
                        onChange={() => onAnswer(q.id, opt)}
                        className="mt-1 accent-primary"
                      />
                    )}
                    <span className="pt-0.5">{choiceLabel(i, opt)}</span>
                  </label>
                );
              })}
            </li>
          );
        })}
      </ol>
    );
  } else {
    // Предложения, короткий ответ, схема — строка с полем внутри.
    body = (
      <ol className="space-y-2 text-sm leading-9 text-on-surface">
        {task.questions.map((q) => (
          <li key={q.id}>{inline(q)}</li>
        ))}
      </ol>
    );
  }

  // Картинка лежит у вопросов (место под неё в схеме именно там), но относится
  // ко всему заданию — берём первую попавшуюся.
  const planImage = task.questions.find((q) => q.image_url)?.image_url;
  // «NB You may use any letter more than once» — своей строкой, как на листе экзамена (matching, T3 30.10).
  const [instruction, nb] = splitNB(task.instruction ?? '');

  return (
    <Card variant="subtle" className="space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Pill variant="primary">{t(TYPE_LABEL_KEY[task.type] ?? task.type)}</Pill>
          {task.group_label && <span className="text-xs text-on-surface-variant">{task.group_label}</span>}
        </div>
        {instruction && <p className="text-sm text-on-surface-variant">{instruction}</p>}
        {nb && <p className="text-sm font-semibold text-on-surface">{nb}</p>}
      </div>
      {planImage && (
        <figure className="overflow-x-auto rounded-lg border border-outline bg-surface/60 p-2">
          <img
            src={planImage}
            alt={task.instruction || t('ieltsMock.planAlt')}
            className="mx-auto h-auto w-full max-w-[720px]"
            loading="lazy"
          />
        </figure>
      )}
      {body}
    </Card>
  );
}
